import React, { useRef, useState } from 'react';
import { ImagePlus, RotateCcw } from 'lucide-react';

// Report images (Vastu Devta Guide, Feng Shui, Zone Analysis, Helix) can be swapped for the user's own
// picture right inside the report's edit mode. The picked file is shrunk first so a project with many
// replaced pictures stays small enough to save (and to fit in browser storage).
const MAX_SIDE = 900;

export const shrinkImageFile = (file, maxSide = MAX_SIDE) => new Promise((resolve, reject) => {
  if (!file || !/^image\//.test(file.type)) { reject(new Error('Please choose an image file (PNG, JPG, WEBP).')); return; }
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Could not read that file.'));
  reader.onload = () => {
    const img = new Image();
    img.onerror = () => reject(new Error('That image could not be opened.'));
    img.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.max(1, Math.round(img.naturalWidth * scale));
      const h = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';            // flatten transparency onto white (the report pages are white)
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

/**
 * Draws a picture "contained" (never stretched) and centred on a white canvas with the same shape as the PDF
 * slot it will sit in, and returns it as a PNG data URL. The report PDFs place images into fixed-size boxes
 * and declare them PNG, so this keeps replaced pictures undistorted.
 */
export const fitImageToBox = (dataUrl, boxW, boxH) => new Promise((resolve) => {
  const im = new Image();
  im.onload = () => {
    try {
      const W = 900;
      const H = Math.max(1, Math.round((W * boxH) / boxW));
      const canvas = document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, W, H);
      const s = Math.min(W / im.naturalWidth, H / im.naturalHeight);
      const dw = im.naturalWidth * s, dh = im.naturalHeight * s;
      ctx.drawImage(im, (W - dw) / 2, (H - dh) / 2, dw, dh);
      resolve(canvas.toDataURL('image/png'));
    } catch { resolve(dataUrl); }
  };
  im.onerror = () => resolve(dataUrl);
  im.src = dataUrl;
});

/**
 * An image with "Replace" (+ "Reset" once replaced) buttons under it.
 *  src        – the image currently shown (custom picture if there is one, else the default)
 *  isCustom   – true when `src` is the user's own picture
 *  onReplace  – (dataUrl) => void
 *  onReset    – () => void   (back to the default picture)
 */
export default function ReplaceableImage({ src, isCustom = false, onReplace, onReset, alt = '', imgStyle = {}, wrapStyle = {} }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [broken, setBroken] = useState(false);

  const pick = () => { setError(''); inputRef.current?.click(); };
  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';             // allow choosing the same file again later
    if (!file) return;
    setBusy(true); setError('');
    try {
      const dataUrl = await shrinkImageFile(file);
      setBroken(false);
      onReplace(dataUrl);
    } catch (err) {
      setError(err.message || 'Could not use that image.');
    } finally {
      setBusy(false);
    }
  };

  const btn = {
    display: 'inline-flex', alignItems: 'center', gap: 5, height: 28, padding: '0 10px', borderRadius: 7,
    border: '1px solid #CFE3E2', background: '#ffffff', color: '#00857E', fontSize: '0.72rem', fontWeight: 600,
    cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, ...wrapStyle }}>
      {!broken && (
        <img
          src={src}
          alt={alt}
          style={{ maxHeight: 190, maxWidth: '100%', objectFit: 'contain', borderRadius: 6, pointerEvents: 'none', userSelect: 'none', ...imgStyle }}
          draggable={false}
          onError={() => setBroken(true)}
        />
      )}
      {broken && (
        <div style={{ width: 140, height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #CBD5E1', borderRadius: 8, color: '#94a3b8', fontSize: '0.7rem' }}>
          No image
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
        <button type="button" style={btn} onClick={pick} disabled={busy} title="Replace this picture with your own">
          <ImagePlus size={13} />{busy ? 'Please wait…' : 'Replace image'}
        </button>
        {isCustom && (
          <button type="button" style={{ ...btn, color: '#64748b', borderColor: '#e2e8f0' }} onClick={() => { setBroken(false); setError(''); onReset && onReset(); }} title="Go back to the original picture">
            <RotateCcw size={12} />Reset
          </button>
        )}
      </div>
      {error && <div style={{ color: '#dc2626', fontSize: '0.7rem', fontWeight: 600 }}>{error}</div>}
      <input ref={inputRef} type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />
    </div>
  );
}
